import React, { useState, useEffect, useRef } from 'react';

interface ImageProps {
  src: string;
  alt: string;
  width?: number;
  height?: number;
  layout?: 'fixed' | 'responsive' | 'fill' | 'intrinsic';
  objectFit?: 'contain' | 'cover' | 'fill' | 'none' | 'scale-down';
  quality?: number;
  priority?: boolean;
  loading?: 'eager' | 'lazy';
  onLoad?: () => void;
  onError?: () => void;
  className?: string;
  style?: React.CSSProperties;
  fill?: boolean;
}

const Image: React.FC<ImageProps> = ({
  src,
  alt,
  width,
  height,
  layout = 'intrinsic',
  objectFit = 'cover',
  quality = 75,
  priority = false,
  loading = 'lazy',
  onLoad,
  onError,
  className = '',
  style = {},
  fill = false,
}) => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [error, setError] = useState(false);
  const imgRef = useRef<HTMLImageElement>(null);

  useEffect(() => {
    if (priority && imgRef.current) {
      const img = imgRef.current;
      img.fetchPriority = 'high';
    }
  }, [priority]);

  const handleLoad = () => {
    setIsLoaded(true);
    onLoad?.();
  };

  const handleError = () => {
    setError(true);
    onError?.();
  };

  const getLayoutStyle = (): React.CSSProperties => {
    if (fill) {
      return {
        position: 'absolute',
        top: 0,
        left: 0,
        bottom: 0,
        right: 0,
        width: '100%',
        height: '100%',
      };
    }

    switch (layout) {
      case 'fixed':
        return {
          width: `${width}px`,
          height: `${height}px`,
        };
      case 'fill':
        return {
          position: 'absolute',
          top: 0,
          left: 0,
          bottom: 0,
          right: 0,
          width: '100%',
          height: '100%',
        };
      case 'responsive':
        return {
          width: '100%',
          height: 'auto',
        };
      case 'intrinsic':
      default:
        return {
          maxWidth: '100%',
          height: 'auto',
        };
    }
  };

  const imgStyle: React.CSSProperties = {
    ...getLayoutStyle(),
    objectFit,
    ...style,
  };

  const wrapperStyle: React.CSSProperties = layout === 'fill' || fill ? { position: 'relative', width: '100%', height: '100%' } : {};

  return (
    <div style={wrapperStyle} className={className}>
      {!error ? (
        <img
          ref={imgRef}
          src={`${src}${src.includes('?') ? '&' : '?'}q=${quality}`}
          alt={alt}
          width={width}
          height={height}
          loading={loading}
          onLoad={handleLoad}
          onError={handleError}
          style={imgStyle}
        />
      ) : (
        <div
          style={{
            ...imgStyle,
            backgroundColor: '#f0f0f0',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
            <span className="text-white text-[0.2rem]">Image failed to load</span>
        </div>
      )}
    </div>
  );
};

export default Image;
